import {
  ShieldCheck,
  Truck,
  Warehouse,
  CarFront,
  Wrench,
  Handshake,
  BadgeCheck,
  Network,
  HeartHandshake,
  UserCheck,
  PoundSterling,
  HeartPulse,
} from "lucide-react";

export const PHONE = "0800 994 9096";
export const PHONE_LINK = "tel:08009949096";
export const EMAIL = "info@sageautogroup.co.uk";
export const EMAIL_LINK = "mailto:info@sageautogroup.co.uk";
export const WEBSITE = "www.sageautogroup.co.uk";
export const WEBSITE_LINK = "https://www.sageautogroup.co.uk";
export const INSTAGRAM_HANDLE = "@sageautogroupuk";
export const INSTAGRAM_LINK = "https://instagram.com/sageautogroupuk";
export const TAGLINE = "One Call. One Team. Complete Peace of Mind.";

export const HERO_CHECKLIST = [
  "Like-for-like replacement vehicles",
  "Non-fault accident claims",
  "Vehicle recovery & secure storage",
  "Personal injury support",
  "Maximising your vehicle settlement",
];

export const HOURS = [
  { days: "Monday – Friday", time: "8:00 AM – 6:00 PM" },
  { days: "Saturday", time: "9:00 AM – 1:00 PM" },
  { days: "Sunday", time: "By Appointment" },
];

export const EMERGENCY_NOTE =
  "Emergency Accident Assistance: 24 Hours (Subject to Partner Availability)";

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1580679568899-be51739ba2df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtZXJjZWRlcyUyMGx1eHVyeSUyMGNhciUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4NzEzNTM1Nnww&ixlib=rb-4.1.0&q=85",
  about: "https://images.pexels.com/photos/17185083/pexels-photo-17185083.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  interior: "https://images.unsplash.com/photo-1633348570573-2c70fe9129de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwb3JzY2hlJTIwZGV0YWlsJTIwZGFya3xlbnwwfHx8fDE3ODcxMzUzNTZ8MA&ixlib=rb-4.1.0&q=85",
  rangeRover: "https://images.unsplash.com/photo-1740695600568-a7e54e7d3614?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjByYW5nZSUyMHJvdmVyJTIwZGFyayUyMG1vb2R8ZW58MHx8fHwxNzg3MTM1MzU3fDA&ixlib=rb-4.1.0&q=85",
  night: "https://images.unsplash.com/photo-1610374634235-b51ef357f905?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY2FyJTIwZHJpdmluZyUyMG5pZ2h0JTIwbHV4dXJ5fGVufDB8fHx8MTc4NzEzNTM1N3ww&ixlib=rb-4.1.0&q=85",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    icon: ShieldCheck,
    title: "Non-Fault Accident Management",
    description:
      "We manage your claim from start to finish — one dedicated specialist coordinating everything so you never chase an insurer, repairer or recovery firm again.",
  },
  {
    icon: CarFront,
    title: "Like-for-Like Replacement Vehicles",
    description:
      "We arrange replacement vehicles to help keep you moving while your own vehicle is repaired or your claim is being resolved.",
  },
  {
    icon: Truck,
    title: "Vehicle Recovery",
    description:
      "Professional recovery through our trusted nationwide network — wherever you are, whatever the hour, subject to partner availability.",
  },
  {
    icon: Warehouse,
    title: "Secure Vehicle Storage",
    description:
      "Safe and secure vehicle storage until your insurer authorises the next stage.",
  },
  {
    icon: PoundSterling,
    title: "Vehicle Valuation Support",
    description:
      "We help ensure your vehicle is fairly valued and assist you throughout the settlement process.",
  },
  {
    icon: HeartPulse,
    title: "Personal Injury Support",
    description:
      "Where appropriate, we can introduce you to specialist legal professionals for personal injury claims.",
  },
  {
    icon: Wrench,
    title: "Repairs",
    description:
      "If repairs are required, we coordinate them through our trusted repair network — genuine parts, returned to pre-accident condition.",
  },
];

export const STEPS = [
  {
    number: "01",
    title: "First Notification",
    text: `Call ${PHONE} the moment it happens. One conversation is all it takes — we capture the details, assess your situation and take immediate ownership of everything that follows.`,
  },
  {
    number: "02",
    title: "Recovery & Safety",
    text: "A trusted recovery operator is dispatched to your location. Your vehicle is recovered professionally and you are never left stranded at the roadside.",
  },
  {
    number: "03",
    title: "Secure Storage",
    text: "Your vehicle is taken to a secure, monitored storage facility while your claim is progressed — protected, documented and ready for independent inspection.",
  },
  {
    number: "04",
    title: "Replacement Vehicle",
    text: "A like-for-like replacement vehicle is arranged and delivered, so your life carries on uninterrupted while your own vehicle is off the road.",
  },
  {
    number: "05",
    title: "Repairs & Coordination",
    text: "We coordinate repairs through manufacturer-approved repairers, managing engineers, estimates and schedules on your behalf until your vehicle is restored.",
  },
  {
    number: "06",
    title: "Claim Resolution",
    text: "We liaise with insurers and legal professionals through to settlement. Costs are recovered from the at-fault party's insurer, and your claim is closed — properly.",
  },
];

export const WHY_CHOOSE = [
  { icon: UserCheck, title: "One Point of Contact", text: "One dedicated specialist from the moment your accident happens until your claim is resolved." },
  { icon: CarFront, title: "Replacement Vehicles", text: "Like-for-like replacement vehicles to keep you moving throughout your claim." },
  { icon: BadgeCheck, title: "Professional Claims Management", text: "Your claim managed from start to finish — correctly, calmly, and without the phone queues." },
  { icon: Network, title: "Nationwide Recovery Network", text: "Trusted recovery operators across the UK, ready when you need them most." },
  { icon: Warehouse, title: "Secure Vehicle Storage", text: "Monitored, secure storage until your insurer authorises the next stage." },
  { icon: Handshake, title: "Insurance Liaison", text: "Every insurer conversation, engineer and estimate handled on your behalf." },
  { icon: HeartHandshake, title: "Stress-Free Process", text: "From the first call to the final settlement — start to finish, we carry it." },
];

export const FAQS = [
  {
    question: "The accident wasn't my fault. Do I have to pay for your services?",
    answer:
      "No. If the accident was not your fault, the costs of our services — including recovery, storage, your replacement vehicle and repairs — are recovered directly from the at-fault party's insurer. You should not be left out of pocket.",
  },
  {
    question: "Will using Sage Auto Group affect my no-claims bonus?",
    answer:
      "For non-fault claims, we pursue the at-fault party's insurer directly rather than claiming on your own policy, which is specifically designed to protect your no-claims bonus and avoid you paying an excess.",
  },
  {
    question: "How quickly can I get a replacement vehicle?",
    answer:
      "In most cases a like-for-like replacement vehicle can be arranged promptly once we have taken your details — often within 24 hours. Our team will confirm timescales for your specific situation on your first call.",
  },
  {
    question: "Do I have to use the repairer my insurance company suggests?",
    answer:
      "No. You have the right to choose who repairs your vehicle. We coordinate repairs through our network of manufacturer-approved repairers, ensuring genuine parts and that your vehicle is returned to its pre-accident condition.",
  },
  {
    question: "What should I do immediately after an accident?",
    answer:
      "Make sure everyone is safe and call emergency services if needed. Exchange details with the other parties, take photographs of the scene and vehicles if it is safe to do so, and do not admit fault. Then call us on 0800 994 9096 — we will take it from there.",
  },
  {
    question: "What information do you need when I call?",
    answer:
      "Just the basics: where and when the accident happened, details of the vehicles involved, and any photographs or reference numbers you may have. Don't worry if you don't have everything — your dedicated contact will guide you through it.",
  },
  {
    question: "Do you operate outside normal office hours?",
    answer:
      "Our office operates Monday to Friday 8:00 AM – 6:00 PM and Saturday 9:00 AM – 1:00 PM, with Sunday by appointment. Emergency accident assistance is available 24 hours, subject to partner availability.",
  },
  {
    question: "Do you cover my area?",
    answer:
      "Yes. We operate through a trusted nationwide network of recovery operators, repairers, hire vehicle providers and legal professionals across the United Kingdom.",
  },
];

export const MARQUEE_ITEMS = [
  "One Call",
  "One Team",
  "Complete Peace of Mind",
  "Non-Fault Accident Management",
  "Replacement Vehicles",
  "Personal Injury Support",
  "Vehicle Valuation Support",
];
